import { jsx } from 'react/jsx-runtime';
import { Component } from 'react';
import Popper from 'popper.js';
import { createPortal } from 'react-dom';
import { AnimatedPopoverInternal } from './styles.js';

const ENTRANCE_ANIMATION_FROM_PLACEMENT = {
  top: 'enterFromBottom',
  right: 'enterFromLeft',
  bottom: 'enterFromTop',
  left: 'enterFromRight'
};
const VARIANT_MAPPER = {
  primary: 'dark',
  secondary: 'outlined',
  warning: 'warning',
  shadow: 'white'
};
const TAIL_SIDE_FROM_PLACEMENT = {
  top: 'bottom',
  left: 'right',
  right: 'left',
  bottom: 'top'
};
const VALID_ARIA_ROLES = ['alert', 'tooltip'];

const isValidRole = role => VALID_ARIA_ROLES.includes(role);

class Popover extends Component {
  constructor(props) {
    super(props);
    this.displayName = 'Popover';

    this.onKeyDown = e => {
      // if escape key is pressed
      const {
        key
      } = e;

      if (key === "Escape"
      /* Escape */
      && e) {
        e.preventDefault();
        this.onToggle(e);
      }
    };

    this.setPopoverRef = el => {
      if (!el) return;
      if (this.props.childRef) this.props.childRef(el);
      this._popoverEl = el;
    };

    this.setArrowRef = el => {
      if (el) this._arrow = el;
    };

    this.handleDocumentClick = e => {
      // do nothing if the popover is not visible
      if (!this._popoverEl) return; // do nothing if the click was inside the popover

      if (e.target instanceof Node && this._popoverEl.contains(e.target)) return; // do nothing if the click was inside the popover's target

      if (this.clickedOnTarget(e)) return;
      this.onToggle(e);
    };

    this.applyStyles = data => {
      const placement = data.placement;
      if (placement === this.state.placement) return data;
      this.setState({
        placement
      });
      return data;
    };

    this.state = {
      placement: props.placement
    };
  }

  componentDidMount() {
    if (this.props.isOpen) {
      this.initializePopper();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.initializePopper({
        animate: true
      });
    }
    /* document is defined, but the linter doesn't think so, hence the rule below */

    /* eslint-disable no-undef */


    if (!this.props.isOpen && prevProps.isOpen) {
      if (this._popperController) this._popperController.destroy();
      document.removeEventListener('click', this.handleDocumentClick, true);
      if (this.props.onClose) this.props.onClose();
    }
  }

  componentWillUnmount() {
    if (this._popperController) this._popperController.destroy();
    document.removeEventListener('click', this.handleDocumentClick, true);
  }

  getPopoverTargetRef() {
    const {
      target
    } = this.props; // Support ids

    if (typeof target === 'string') return document.querySelector(`#${target}`); // Support old and new ref APIs.

    const targetRef = target instanceof Element ? target : target === null || target === void 0 ? void 0 : target.current;
    return targetRef;
  }

  onToggle(e) {
    const {
      toggle
    } = this.props; // (e.target instanceof HTMLElement && target.contains(e.target)

    if (toggle) toggle(e);
  }

  initializePopper(optionsIn) {
    const DEFAULTS = {
      animate: false
    };
    const options = Object.assign(DEFAULTS, optionsIn);
    const target = this.getPopoverTargetRef();
    const propsPlacement = this.props.placement;
    const placement = propsPlacement === 'top' || propsPlacement === 'right' || propsPlacement === 'left' || propsPlacement === 'bottom' ? propsPlacement : 'top';
    if (!target) return; // Visually hide the element while it positions

    this._popoverEl.style.visibility = 'hidden';

    this._popoverEl.setAttribute('aria-hidden', 'true');

    const flipConfig = {};

    if (!this.props.flip) {
      flipConfig.flip = {
        enabled: false
      };
    } else if (this.props.boundaryPadding) {
      flipConfig.flip = {
        padding: this.props.boundaryPadding
      };
    } // Initialize Popper


    this._popperController = new Popper(target, this._popoverEl, {
      placement,
      modifiers: {
        _arrow: {
          element: this._arrow
        },
        applyReactStyles: {
          enabled: true,
          fn: this.applyStyles,
          order: 900
        },
        offset: this.props.offset ? {
          enabled: true,
          offset: this.props.offset
        } : undefined,
        ...flipConfig,
        preventOverflow: {
          enabled: true
        }
      },
      onCreate: () => {
        if (!this.props.noAnimation && options.animate) {
          this._popoverEl.style.animationName = `${ENTRANCE_ANIMATION_FROM_PLACEMENT[placement]}, disableMouse`;
          this._popoverEl.style.animationDuration = '150ms';
          this._popoverEl.style.animationTimingFunction = 'ease';
        }

        this._popoverEl.style.visibility = 'visible';

        this._popoverEl.removeAttribute('aria-hidden');

        if (this.props.onOpen) this.props.onOpen();
      }
    });

    this._popperController.scheduleUpdate();

    document.addEventListener('click', this.handleDocumentClick, true);
  }

  clickedOnTarget(e) {
    const target = this.getPopoverTargetRef();
    return target && (target === e.target || e.target instanceof HTMLElement && target.contains(e.target));
  }

  render() {
    const {
      children,
      className,
      containerFluid,
      childRef,
      isOpen,
      onClose,
      onOpen,
      noAnimation,
      placement,
      role = 'tooltip',
      renderInline,
      tailAlignment,
      tailPosition,
      target,
      toggle,
      // removing from spread
      flip,
      // removing from spread
      variant = 'warning',
      isDismissible,
      onDismiss,
      zIndex = 20,
      ...restProps
    } = this.props;
    if (!isOpen) return null;

    const speechBubble = jsx(AnimatedPopoverInternal, Object.assign({
      attributes: restProps,
      bubbleRef: this.setPopoverRef,
      className: className,
      containerFluid: containerFluid,
      inline: true,
      isDismissible: isDismissible,
      maxWidth: "275px",
      onDismiss: onDismiss,
      onKeyDown: this.onKeyDown,
      role: isValidRole(role) ? role : 'tooltip',
      tailAlignment: tailAlignment ? tailAlignment : tailPosition ? {
        origin: tailPosition,
        offset: 0
      } : undefined,
      tailRef: this.setArrowRef,
      tailSide: this.state.placement && TAIL_SIDE_FROM_PLACEMENT[this.state.placement],
      tailSize: "sm",
      variant: variant === 'primary' || variant === 'secondary' || variant === 'warning' || variant === 'shadow' ? VARIANT_MAPPER[variant] : 'warning',
      zIndex: zIndex
    }, {
      children: children
    }), void 0);

    if (renderInline) return speechBubble; // Check for the window's existence to avoid typeerrors that occur when checking for the document during SSR

    if (typeof window === 'undefined') return null;
    const AppRootEl = document === null || document === void 0 ? void 0 : document.querySelector('body');
    if (!AppRootEl) return null;
    return createPortal(speechBubble, AppRootEl);
  }

}

Popover.defaultProps = {
  flip: false,
  isOpen: false,
  noAnimation: false,
  placement: 'top',
  renderInline: false,
  role: 'tooltip',
  variant: 'warning'
};

export { Popover, TAIL_SIDE_FROM_PLACEMENT, VALID_ARIA_ROLES, VARIANT_MAPPER };
